import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { Notice } from '@/components/admin/Notice';
import * as authApi from '@/features/auth/api';
import { ApiError } from '@/lib/api/http';

/**
 * ログイン画面（受講生・理事・講師・サクセス担当専用）。
 * 事務局・スーパー管理者は別URL（`/staff/login`）を使う（管理系アカウントの
 * ログイン導線を分ける方が望ましいという利用者フィードバックを受けて分離）。
 */
export function LoginPage(): ReactElement {
  const { user, needsTotp, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate(needsTotp ? '/totp' : '/', { replace: true });
  }, [user, needsTotp, navigate]);

  async function handle(action: () => Promise<authApi.LoginResult>): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const result = await action();
      setUser(result.user);
      navigate(result.user.totp_required && !result.user.totp_verified ? '/totp' : '/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'ログインに失敗しました。');
    } finally {
      setSubmitting(false);
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void handle(() => authApi.login(email, password));
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-wpcanvas px-4">
      <div className="w-full max-w-sm">
        <h1 className="mb-6 text-center text-xl font-semibold text-wptext-strong">CXO GROWTH</h1>

        <form
          onSubmit={onSubmit}
          className="rounded-sm border border-wpborder bg-white px-5 py-6 shadow-sm"
        >
          {error && (
            <Notice kind="error" dismissible={false}>
              {error}
            </Notice>
          )}

          <label htmlFor="login-email" className="mb-1.5 block text-wptext-muted">
            メールアドレス
          </label>
          <input
            id="login-email"
            name="email"
            // type="email" にはしない（デモ用の `student@demo` 等 RFC 非準拠の値も扱うため / D-006）。
            // スマホでメール向けキーボードが出るように inputMode だけ指定する（D-083）
            type="text"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mb-4 w-full rounded-sm border border-wpborder px-3 py-2"
          />

          <label htmlFor="login-password" className="mb-1.5 block text-wptext-muted">
            パスワード
          </label>
          <div className="relative mb-5">
            <input
              id="login-password"
              name="password"
              type={passwordVisible ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-sm border border-wpborder py-1.5 pl-2 pr-16"
            />
            {/* 打ち間違いに気づけるようにする（入力できない・ログインできないの多くはこれ / D-083） */}
            <button
              type="button"
              onClick={() => setPasswordVisible((visible) => !visible)}
              className="absolute inset-y-0 right-0 px-2 text-xs text-wpaccent hover:underline"
            >
              {passwordVisible ? '隠す' : '表示'}
            </button>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-sm bg-wpaccent px-3 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {submitting ? 'ログイン中…' : 'ログイン'}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-wptext-muted">
          <Link to="/password/reset-request" className="text-wpaccent hover:underline">
            パスワードをお忘れの方
          </Link>
        </p>
        <p className="mt-2 text-center text-xs text-wptext-muted">
          事務局・スーパー管理者の方は
          <Link to="/staff/login" className="text-wpaccent hover:underline">
            こちら
          </Link>
        </p>
        <p className="mt-2 text-center text-xs text-wptext-muted">
          アカウントは招待制です。お持ちでない場合は事務局にご連絡ください。
        </p>
      </div>
    </div>
  );
}
